// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplateJobs, useInstanceCredentials } from '../../hooks/useInstances'
import { useUpgradeStatus } from '../../hooks/useUpgrade'
import { useUpdateInstanceSettings } from '../../hooks/useAddons'
import { useCreateTemplateFromInstance } from '../../hooks/useTemplates'
import { useAuthStore } from '../../store/authStore'
import type { Instance } from '../../api/client'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Field,
  Input,
  LinkButton,
  Textarea,
  cx,
  type BadgeTone,
} from '../../components/ui'
import { CopyButton, extractApiError } from './shared'

export function OverviewTab({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const settingsMutation = useUpdateInstanceSettings(instance.id)
  const [settingsError, setSettingsError] = useState('')
  const role = useAuthStore((s) => s.role)
  const rawPlan = useAuthStore((s) => s.plan)
  const plan = rawPlan ?? 'free'
  const isFreeNonAdmin = plan === 'free' && role !== 'admin'

  return (
    <div className="space-y-4">
      <ProvisioningCard instance={instance} />
      <Card>
        <CardHeader title={t('detail.overview.urls')} className="mb-3" />
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-gray-500">{t('detail.overview.primaryUrl')}</span>
            <div className="flex items-center gap-2 min-w-0">
              <a href={instance.subdomain_url} target="_blank" rel="noopener noreferrer"
                className="text-brand-600 hover:text-brand-700 truncate min-w-0">{instance.subdomain_url}</a>
              <CopyButton text={instance.subdomain_url} />
            </div>
          </div>
          {instance.custom_url && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="text-gray-500">{t('detail.overview.customDomain')}</span>
              <div className="flex items-center gap-2 min-w-0">
                <a href={instance.custom_url} target="_blank" rel="noopener noreferrer"
                  className="text-brand-600 hover:text-brand-700 truncate min-w-0">{instance.custom_url}</a>
                <CopyButton text={instance.custom_url} />
              </div>
            </div>
          )}
          <div className="border-t border-gray-100 pt-2 mt-2" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('detail.overview.database')}</span>
            <span className="font-mono text-xs text-gray-700">{instance.db_name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('detail.overview.odooVersion')}</span>
            <span className="font-mono text-xs text-gray-700">v{instance.odoo_version}</span>
          </div>
          <VersionRows instance={instance} />
        </div>
      </Card>

      <AdminCredentialsCard instance={instance} />

      <Card>
        <CardHeader title={t('detail.overview.settings')} className="mb-3" />
        {/* row-reverse keeps the control on the right — the layout this row has
            always had - while `Checkbox` supplies the label/input wiring. */}
        <Checkbox
          className={cx(
            'flex-row-reverse justify-between gap-3 select-none',
            isFreeNonAdmin ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
          checked={instance.auto_db_restore_on_failure}
          disabled={isFreeNonAdmin}
          onChange={(e) => {
            setSettingsError('')
            settingsMutation.mutate(
              { auto_db_restore_on_failure: e.target.checked },
              { onError: (err) => setSettingsError(extractApiError(err) ?? t('common.error')) },
            )
          }}
          label={
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 font-medium">{t('detail.overview.autoRestore')}</span>
                {isFreeNonAdmin && (
                  <Badge tone="accent" className="uppercase tracking-wide">{t('detail.proBadge')}</Badge>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{t('detail.overview.autoRestoreHint')}</p>
            </div>
          }
        />
        {settingsError && <Alert tone="danger" className="mt-3">{settingsError}</Alert>}
      </Card>

      {role === 'admin' && <CreateTemplateFromInstanceCard instance={instance} />}
    </div>
  )
}

export function AdminCredentialsCard({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState(false)
  const ready = instance.status === 'running'
  const { data, isFetching, isError, error } = useInstanceCredentials(instance.id, revealed && ready)
  const apiErr = extractApiError(error)
  const notStored = apiErr === 'credentials_not_stored'

  return (
    <Card>
      <CardHeader
        title={t('detail.overview.credentials')}
        description={t('detail.overview.credentialsHint')}
        className="mb-4"
      />
      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-gray-500">{t('detail.overview.login')}</span>
          <div className="flex items-center gap-2">
            <span className="font-mono">admin</span>
            <CopyButton text="admin" />
          </div>
        </div>
        <div className="flex justify-between items-center gap-3">
          <span className="text-gray-500 shrink-0">{t('detail.overview.password')}</span>
          {!revealed ? (
            <LinkButton tone="accent" onClick={() => setRevealed(true)} disabled={!ready}>
              {t('detail.overview.revealPassword')}
            </LinkButton>
          ) : isFetching ? (
            <span className="text-xs text-gray-500">{t('common.loading')}</span>
          ) : isError ? (
            <span className="text-xs text-red-600">
              {notStored ? t('detail.overview.credentialsUnavailable') : (apiErr ?? t('detail.overview.credentialsError'))}
            </span>
          ) : data ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1 break-all">{data.admin_password}</span>
              <CopyButton text={data.admin_password} />
            </div>
          ) : null}
        </div>
      </div>
      {!ready && (
        <p className="text-xs text-gray-400 mt-3">{t('detail.overview.resetRequiresRunning')}</p>
      )}
    </Card>
  )
}

export function CreateTemplateFromInstanceCard({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const createMutation = useCreateTemplateFromInstance()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ captured: number; installed: number; skipped: string[]; templateName: string } | null>(null)

  const canSubmit = name.trim().length > 0 && instance.status === 'running'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    createMutation.mutate(
      {
        instanceId: instance.id,
        body: { name: name.trim(), description: description.trim() || undefined },
      },
      {
        onSuccess: (res) => {
          setResult({
            captured: res.captured_count,
            installed: res.installed_count,
            skipped: res.skipped_modules ?? [],
            templateName: res.template.name,
          })
          setName('')
          setDescription('')
          setOpen(false)
        },
        onError: (err) => setError(extractApiError(err) ?? t('detail.templateFromInstance.submitError')),
      },
    )
  }

  return (
    <Card>
      <CardHeader
        title={t('detail.templateFromInstance.title')}
        description={t('detail.templateFromInstance.description')}
        actions={!open && !result ? (
          <Button
            onClick={() => setOpen(true)}
            disabled={instance.status !== 'running'}
            title={instance.status !== 'running' ? t('detail.templateFromInstance.instanceMustRun') : ''}
          >
            {t('detail.templateFromInstance.createBtn')}
          </Button>
        ) : undefined}
      />

      {open && !result && (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label={t('detail.templateFromInstance.nameLabel')} required>
            {(props) => (
              <Input
                {...props}
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('detail.templateFromInstance.namePlaceholder')}
              />
            )}
          </Field>
          <Field label={t('detail.templateFromInstance.descriptionLabel')}>
            {(props) => (
              <Textarea
                {...props}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            )}
          </Field>
          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={!canSubmit} loading={createMutation.isPending}>
              {t('common.create')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setOpen(false); setError(''); setName(''); setDescription('') }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}

      {result && (
        <Alert tone="success" className="mt-4">
          <p
            className="font-medium"
            dangerouslySetInnerHTML={{ __html: t('detail.templateFromInstance.createdNotice', { name: result.templateName }).replace('<span>', '<span class="font-mono">') }}
          />
          <p className="mt-1">
            {t('detail.templateFromInstance.createdSummary', { captured: result.captured, installed: result.installed })}
          </p>
          {result.skipped.length > 0 && (
            <div className="mt-2 text-amber-700">
              <p className="font-medium">{t('detail.templateFromInstance.skippedTitle')}</p>
              <p className="font-mono text-xs mt-1">{result.skipped.join(', ')}</p>
            </div>
          )}
          <LinkButton tone="success" className="mt-3 block" onClick={() => setResult(null)}>
            {t('common.dismiss')}
          </LinkButton>
        </Alert>
      )}
    </Card>
  )
}

// ── Provisioning card (template install jobs, rendered inside Overview) ─────

// Each job status maps onto one of the five badge roles. downloading/installing
// were `blue`, which the palette contract folds into the accent role (`brand`).
const templateJobTone: Record<string, BadgeTone> = {
  queued:      'neutral',
  downloading: 'accent',
  installing:  'accent',
  success:     'success',
  failed:      'danger',
}

export function ProvisioningCard({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const poll = instance.status === 'provisioning'
  const { data: jobs } = useTemplateJobs(instance.id, poll)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  if (!jobs || jobs.length === 0) return null

  return (
    <Card padded={false}>
      <div className="px-5 py-4 border-b border-gray-100">
        <CardHeader title={t('detail.setup.title')} description={t('detail.setup.subtitle')} />
      </div>
      <div className="divide-y divide-gray-100">
        {jobs.map((job) => {
          const isExpanded = !!expanded[job.id]
          return (
            <div key={job.id} className="px-5 py-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge tone={templateJobTone[job.status] ?? 'neutral'}>
                  {t(`detail.setup.status.${job.status}`, job.status)}
                </Badge>
                <span className="font-medium text-gray-900 text-sm">{job.module_name}</span>
                {job.attempt > 1 && (
                  <span className="text-xs text-gray-400">
                    {t('detail.setup.attempt', { n: job.attempt })}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1 truncate">
                <a href={job.github_url} target="_blank" rel="noopener noreferrer" className="hover:text-brand-600">
                  {job.github_url}
                </a>
                <span className="text-gray-400"> · {job.branch}</span>
              </p>
              {job.logs && (
                <div className="mt-2">
                  <LinkButton
                    tone="accent"
                    onClick={() => setExpanded((s) => ({ ...s, [job.id]: !isExpanded }))}
                  >
                    {isExpanded ? t('detail.setup.hideLogs') : t('detail.setup.showLogs')}
                  </LinkButton>
                  {isExpanded && (
                    <pre className="mt-2 bg-ink-900 text-ink-200 rounded p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-auto">
                      {job.logs}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

/** A pinned tag ends in a release date; anything else moves with upstream. */
const PINNED_TAG = /-\d{8}$/

/**
 * The image the instance runs and, when one is open, where its major-version
 * upgrade stands. Visible to the owner too: the rehearsal clone is theirs to
 * test, and a production run takes their instance down.
 */
function VersionRows({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const upgrade = useUpgradeStatus(instance.id)
  const u = upgrade.data?.upgrade
  const open = u && !['committed', 'rolled_back', 'failed', 'cancelled'].includes(u.state)
  const rehearsal = upgrade.data?.rehearsal

  return (
    <>
      {instance.odoo_image_ref && (
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <span className="text-gray-500">{t('detail.overview.image')}</span>
          <span className="flex items-center gap-2">
            {!PINNED_TAG.test(instance.odoo_image_ref) && (
              <Badge tone="warning">{t('detail.overview.rollingTag')}</Badge>
            )}
            <span className="font-mono text-xs text-gray-700">{instance.odoo_image_ref}</span>
          </span>
        </div>
      )}
      {open && (
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <span className="text-gray-500">{t('detail.overview.upgrade')}</span>
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={u.state === 'observing' ? 'accent' : 'warning'}>
              {t('detail.overview.upgradeTo', { to: u.to_series })} · {t(`detail.upgrade.state.${u.state}`)}
            </Badge>
            {rehearsal && u.state !== 'observing' && (
              <a href={rehearsal.url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-brand-600 hover:text-brand-700">{t('detail.overview.testCopy')}</a>
            )}
          </span>
        </div>
      )}
    </>
  )
}
